import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  IconButton,
  InputText,
  InputTextarea,
  Label,
  Modal,
  Stepper,
} from '@teo/components';
import {
  CrossFilled,
  Document,
  Pencil,
  Trash,
  Upload,
} from '@teo/components/icons';

import { axios } from '@/axios';
import { config } from '@/config';
import ComponentDeleteModal from '@/components/DeleteModal/ComponentDeleteModal';
import { PaginationMultiSelect } from '@/components/PaginationMultiSelect';
import { PaginationSingleSelect } from '@/components/PaginationSingleSelect';
import { T } from '@/components/T';
import { NavContext } from '@/components/NavContext';
import { useGet } from '@/query';
import {
  CompetenceValidityDuration,
  CompetenceValidityFields,
  defaultCompetenceExpiryDate,
  serializeCompetenceValidity,
} from '@/components/Competencies/CompetenceValidityFields';
import { useMobile } from '@/util';
import '@/components/Froala/widgets/ChatGPT/ChatGPT.css';

type SuggestionAction = 'create' | 'match';

export type CategorySuggestion = {
  ref: string;
  action: SuggestionAction;
  existingId?: number;
  name: string;
  description: string;
  code: string;
};

export type ProfileSuggestion = {
  ref: string;
  action: SuggestionAction;
  entityType: 'profile';
  name: string;
  description: string;
  code: string;
};

export type CompetenceSuggestion = {
  ref: string;
  action: SuggestionAction;
  categoryRef: string;
  profileRefs: string[];
  name: string;
  description: string;
  code: string;
  expiryDate?: string | null;
  validForDays?: number | null;
};

export type ImportPreview = {
  importId: string;
  mode: 'ai';
  source: {
    fileName: string;
    fileSize: number;
    extension: string;
  };
  summary: {
    categories: number;
    profiles: number;
    competences: number;
  };
  categories: CategorySuggestion[];
  profiles: ProfileSuggestion[];
  competences: CompetenceSuggestion[];
};

type ImportResult = {
  importId: string;
  status: 'completed';
  replayed: boolean;
  summary: {
    categoriesCreated: number;
    categoriesMatched: number;
    profilesCreated: number;
    competencesCreated: number;
    linksCreated: number;
    teoLinksCreated?: number;
  };
};

type TeoCompetence = {
  id: number;
  code: string;
  name: string;
  description: string;
  type: string;
};

type TeoLinkSuggestion = {
  competenceRef: string;
  teoCompetenceIds: number[];
};

type TeoLinkPreview = {
  links: TeoLinkSuggestion[];
  teoCompetences: TeoCompetence[];
};

export type PreviewItem =
  | CategorySuggestion
  | ProfileSuggestion
  | CompetenceSuggestion;
type EditableSuggestion = PreviewItem;

type ExistingCategory = {
  id: number;
  name: string;
  description?: string | null;
  code?: string | null;
  codePrefix?: string | null;
  colorCode?: string | null;
};

type CategoryOption = {
  id: string;
  ref: string;
  name: string;
  color?: string | null;
  source: 'ai' | 'existing';
  existingCategory?: ExistingCategory;
};

type UploadRequestError = {
  response?: {
    data?: {
      message?: string;
    };
  };
};

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getExtension(fileName: string) {
  return fileName.split('.').pop()?.toLowerCase() || '';
}

function isCompetence(item: PreviewItem): item is CompetenceSuggestion {
  return 'profileRefs' in item;
}

function isProfile(item: PreviewItem): item is ProfileSuggestion {
  return 'entityType' in item && item.entityType === 'profile';
}

function pruneUnusedMatchedCategories(preview: ImportPreview): ImportPreview {
  const referencedCategoryRefs = new Set([
    ...preview.competences.map(({ categoryRef }) => categoryRef),
  ]);

  return {
    ...preview,
    categories: preview.categories.filter(
      (category) =>
        category.action === 'create' || referencedCategoryRefs.has(category.ref)
    ),
  };
}

export function removePreviewItem(
  preview: ImportPreview,
  item: PreviewItem,
  categoryReassignments: Record<string, string> = {}
): ImportPreview {
  if (isCompetence(item)) {
    return pruneUnusedMatchedCategories({
      ...preview,
      competences: preview.competences.filter(
        (competence) => competence.ref !== item.ref
      ),
    });
  }

  if (isProfile(item)) {
    return pruneUnusedMatchedCategories({
      ...preview,
      profiles: preview.profiles.filter((profile) => profile.ref !== item.ref),
      competences: preview.competences
        .map((competence) => ({
          ...competence,
          profileRefs: competence.profileRefs.filter(
            (profileRef) => profileRef !== item.ref
          ),
        }))
        .filter((competence) => competence.profileRefs.length > 0),
    });
  }

  return pruneUnusedMatchedCategories({
    ...preview,
    categories: preview.categories.filter(
      (category) => category.ref !== item.ref
    ),
    profiles: preview.profiles,
    competences: preview.competences.map((competence) => ({
      ...competence,
      categoryRef:
        competence.categoryRef === item.ref
          ? categoryReassignments[competence.ref] || competence.categoryRef
          : competence.categoryRef,
    })),
  });
}

function addMatchedCategory(
  preview: ImportPreview,
  selectedCategory?: CategoryOption
): ImportPreview {
  if (
    selectedCategory?.source !== 'existing' ||
    !selectedCategory.existingCategory ||
    preview.categories.some(({ ref }) => ref === selectedCategory.ref)
  ) {
    return preview;
  }

  const existingCategory = selectedCategory.existingCategory;
  return {
    ...preview,
    categories: [
      ...preview.categories,
      {
        ref: selectedCategory.ref,
        action: 'match',
        existingId: existingCategory.id,
        name: existingCategory.name,
        description: existingCategory.description || '',
        code:
          existingCategory.codePrefix ||
          existingCategory.code ||
          `CAT-${existingCategory.id}`,
      },
    ],
  };
}

function SuggestionCard({
  item,
  categoryName,
  profileNames,
  competenceNames,
  onEdit,
  onDelete,
}: {
  item: PreviewItem;
  categoryName?: string;
  profileNames?: string[];
  competenceNames?: string[];
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const itemIsCompetence = isCompetence(item);
  const validity = itemIsCompetence
    ? item.validForDays
      ? t('pages.manage_competencies.import.validity_days', {
          days: item.validForDays,
        })
      : item.expiryDate
      ? t('pages.manage_competencies.import.validity_until', {
          date: new Intl.DateTimeFormat(i18n.language).format(
            new Date(item.expiryDate)
          ),
        })
      : t('pages.follow_up_users.manual_assign_deny_duration_forever')
    : null;

  return (
    <div className="rounded-xl border border-grey-02 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="font-mono rounded bg-grey-01 px-2 py-1 text-xs font-semibold text-grey-07">
              {item.code}
            </span>
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                item.action === 'match'
                  ? 'bg-[#e9f7ef] text-[#237a4b]'
                  : 'bg-[#eef4ff] text-[#315da8]'
              }`}
            >
              {item.action === 'match'
                ? t('pages.manage_competencies.import.matched')
                : t('pages.manage_competencies.import.new')}
            </span>
          </div>
          <h3 className="text-sm font-semibold text-black">{item.name}</h3>
          <p className="mt-1 text-sm leading-5 text-grey-06">
            {item.description}
          </p>
          {categoryName && (
            <p className="mt-3 text-xs text-grey-06">
              <span className="font-semibold text-grey-07">
                {t('pages.manage_competencies.import.category')}:
              </span>{' '}
              {categoryName}
            </p>
          )}
          {validity && (
            <p className="mt-1 text-xs text-grey-06">
              <span className="font-semibold text-grey-07">
                {t('pages.follow_up_users.expiration_date')}:
              </span>{' '}
              {validity}
            </p>
          )}
          {!!profileNames?.length && (
            <p className="mt-1 text-xs text-grey-06">
              <span className="font-semibold text-grey-07">
                {t('pages.manage_competencies.import.profiles')}:
              </span>{' '}
              {profileNames.join(', ')}
            </p>
          )}
          {!!competenceNames?.length && (
            <p className="mt-1 text-xs text-grey-06">
              <span className="font-semibold text-grey-07">
                {t('pages.manage_competencies.import.linked_competences')}:
              </span>{' '}
              {competenceNames.join(', ')}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {onEdit && (
            <IconButton
              Icon={Pencil}
              variant="ghost"
              onClick={onEdit}
              aria-label={t('pages.manage_competencies.import.edit')}
            />
          )}
          {onDelete && (
            <IconButton
              className="text-grey-05"
              Icon={Trash}
              variant="ghost"
              onClick={onDelete}
              aria-label={t('pages.manage_competencies.import.delete')}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function CompetencyImportPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { organisationId } = useParams();
  const { setNavDisabled } = useContext(NavContext);
  const isMobile = useMobile();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [existingCategories, setExistingCategories] = useState<
    ExistingCategory[]
  >([]);
  const [editItem, setEditItem] = useState<EditableSuggestion | null>(null);
  const [editError, setEditError] = useState('');
  const [deleteItem, setDeleteItem] = useState<PreviewItem | null>(null);
  const [categoryReassignments, setCategoryReassignments] = useState<
    Record<string, string>
  >({});
  const [categoryReassignmentOptions, setCategoryReassignmentOptions] =
    useState<Record<string, CategoryOption>>({});
  const [selectedEditCategory, setSelectedEditCategory] =
    useState<CategoryOption>();
  const [editValidityDuration, setEditValidityDuration] =
    useState<CompetenceValidityDuration>('forever');
  const [editValidForDays, setEditValidForDays] = useState(90);
  const [editExpiryDate, setEditExpiryDate] = useState<Date | undefined>(
    defaultCompetenceExpiryDate
  );
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [activeStep, setActiveStep] = useState<'review' | 'teo-links'>(
    'review'
  );
  const [isLinkingTeoCompetences, setIsLinkingTeoCompetences] = useState(false);
  const [teoLinkError, setTeoLinkError] = useState('');
  const [teoCompetences, setTeoCompetences] = useState<TeoCompetence[]>([]);
  const [teoLinks, setTeoLinks] = useState<Record<string, number[]>>({});
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  useEffect(() => {
    setNavDisabled(true);
    return () => setNavDisabled(false);
  }, [setNavDisabled]);

  useGet<ExistingCategory[]>('/competencecategories', {
    enabled: !!organisationId,
    organisationId: `${organisationId},OR,organisationId=null`,
    lang: i18n.language,
    sort: 'name',
    onSuccess: (result: { data?: ExistingCategory[] }) => {
      setExistingCategories(result?.data || []);
    },
  });

  const closePage = () => navigate('..');

  const selectFile = (selectedFile?: File) => {
    setError('');
    setPreview(null);
    setActiveStep('review');
    setTeoLinkError('');
    setTeoCompetences([]);
    setTeoLinks({});

    if (!selectedFile) return;

    setFile(selectedFile);
  };

  const createPreview = async () => {
    if (!file || !organisationId) return;

    setError('');
    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post<ImportPreview>(
        `${config.backend}/import/competencies/${organisationId}/preview`,
        formData
      );
      setPreview(response.data);
    } catch (requestError: unknown) {
      const uploadRequestError = requestError as UploadRequestError;
      setError(
        uploadRequestError.response?.data?.message ||
          t('pages.manage_competencies.import.upload_error')
      );
    } finally {
      setIsUploading(false);
    }
  };

  const reset = () => {
    setPreview(null);
    setFile(null);
    setError('');
    setImportError('');
    setImportResult(null);
    setIsConfirmOpen(false);
    setEditItem(null);
    setDeleteItem(null);
    setCategoryReassignments({});
    setCategoryReassignmentOptions({});
    setSelectedEditCategory(undefined);
    setActiveStep('review');
    setIsLinkingTeoCompetences(false);
    setTeoLinkError('');
    setTeoCompetences([]);
    setTeoLinks({});
    if (inputRef.current) inputRef.current.value = '';
  };

  const openEditor = (item: EditableSuggestion) => {
    setEditItem({ ...item });
    setEditError('');
    setSelectedEditCategory(undefined);
    if ('profileRefs' in item) {
      setEditValidityDuration(
        item.validForDays ? 'days' : item.expiryDate ? 'date' : 'forever'
      );
      setEditValidForDays(item.validForDays || 90);
      setEditExpiryDate(
        item.expiryDate
          ? new Date(item.expiryDate)
          : defaultCompetenceExpiryDate()
      );
    }
  };

  const closeEditor = () => {
    setEditItem(null);
    setEditError('');
    setSelectedEditCategory(undefined);
  };

  const startTeoLinking = async () => {
    if (!preview || !organisationId || !preview.competences.length) return;

    setTeoLinkError('');
    setIsLinkingTeoCompetences(true);
    try {
      const response = await axios.post<TeoLinkPreview>(
        `${config.backend}/import/competencies/${organisationId}/teo-links`,
        { competences: preview.competences }
      );
      setTeoCompetences(response.data.teoCompetences || []);
      setTeoLinks(
        Object.fromEntries(
          (response.data.links || []).map(
            ({ competenceRef, teoCompetenceIds }) => [
              competenceRef,
              teoCompetenceIds,
            ]
          )
        )
      );
      setActiveStep('teo-links');
    } catch (requestError: unknown) {
      const importRequestError = requestError as UploadRequestError;
      setTeoLinkError(
        importRequestError.response?.data?.message ||
          t('pages.manage_competencies.import.teo_links_error')
      );
    } finally {
      setIsLinkingTeoCompetences(false);
    }
  };

  const openDelete = (item: PreviewItem) => {
    setDeleteItem(item);
    setCategoryReassignmentOptions({});
    setCategoryReassignments(
      !isCompetence(item) && !isProfile(item) && preview
        ? Object.fromEntries(
            preview.competences
              .filter((competence) => competence.categoryRef === item.ref)
              .map((competence) => [competence.ref, ''])
          )
        : {}
    );
  };

  const saveEdit = () => {
    if (!editItem || !preview) return;

    const name = editItem.name.trim();
    const code = editItem.code.trim();
    if (!name || !code) {
      setEditError(t('pages.manage_competencies.import.required_fields'));
      return;
    }

    const suggestions = isCompetence(editItem)
      ? preview.competences
      : isProfile(editItem)
      ? preview.profiles
      : preview.categories;
    const duplicateCode = suggestions.some(
      (suggestion) =>
        suggestion.ref !== editItem.ref &&
        suggestion.code.trim().toLowerCase() === code.toLowerCase()
    );
    if (duplicateCode) {
      setEditError(t('pages.manage_competencies.import.duplicate_code'));
      return;
    }

    const updatedItem: EditableSuggestion =
      'profileRefs' in editItem
        ? {
            ...editItem,
            ...serializeCompetenceValidity({
              duration: editValidityDuration,
              expiryDate: editExpiryDate,
              validForDays: editValidForDays,
            }),
            name,
            code,
            description: editItem.description.trim(),
          }
        : {
            ...editItem,
            name,
            code,
            description: editItem.description.trim(),
          };

    setPreview((currentPreview) => {
      if (!currentPreview) return currentPreview;

      let updatedPreview: ImportPreview;

      if (isCompetence(updatedItem)) {
        updatedPreview = {
          ...currentPreview,
          competences: currentPreview.competences.map((competence) =>
            competence.ref === updatedItem.ref ? updatedItem : competence
          ),
        };
      } else if (isProfile(updatedItem)) {
        updatedPreview = {
          ...currentPreview,
          profiles: currentPreview.profiles.map((profile) =>
            profile.ref === updatedItem.ref ? updatedItem : profile
          ),
        };
      } else {
        updatedPreview = {
          ...currentPreview,
          categories: currentPreview.categories.map((category) =>
            category.ref === updatedItem.ref ? updatedItem : category
          ),
        };
      }

      if (isCompetence(updatedItem)) {
        const selectedCategory =
          selectedEditCategory ||
          categoryOptions.find(({ ref }) => ref === updatedItem.categoryRef);
        updatedPreview = addMatchedCategory(updatedPreview, selectedCategory);
      }

      return pruneUnusedMatchedCategories(updatedPreview);
    });

    closeEditor();
    setTeoLinks({});
  };

  const confirmImport = async () => {
    if (!preview || !organisationId || importResult) return;

    setImportError('');
    setIsImporting(true);
    try {
      const response = await axios.post<ImportResult>(
        `${config.backend}/import/competencies/${organisationId}/confirm`,
        {
          importId: preview.importId,
          lang: i18n.language,
          categories: preview.categories,
          profiles: preview.profiles,
          competences: preview.competences,
          teoLinks: Object.entries(teoLinks).map(
            ([competenceRef, teoCompetenceIds]) => ({
              competenceRef,
              teoCompetenceIds,
            })
          ),
        }
      );
      setImportResult(response.data);
      setIsConfirmOpen(false);
      navigate('..', { replace: true });
    } catch (requestError: unknown) {
      const importRequestError = requestError as UploadRequestError;
      setImportError(
        importRequestError.response?.data?.message ||
          t('pages.manage_competencies.import.import_error')
      );
      setIsConfirmOpen(false);
    } finally {
      setIsImporting(false);
    }
  };

  const categoryName = (ref: string) =>
    preview?.categories.find((category) => category.ref === ref)?.name;
  const profileNames = (refs: string[]) =>
    refs
      .map(
        (ref) => preview?.profiles.find((profile) => profile.ref === ref)?.name
      )
      .filter((name): name is string => !!name);
  const competenceNames = (profileRef: string) =>
    preview?.competences
      .filter((competence) => competence.profileRefs.includes(profileRef))
      .map((competence) => competence.name) || [];

  const existingCategoryOption = (
    existingCategory: ExistingCategory
  ): CategoryOption => {
    const matchedCategory = preview?.categories.find(
      (category) =>
        category.action === 'match' &&
        category.existingId === existingCategory.id
    );

    return {
      id: `existing-${existingCategory.id}`,
      ref: matchedCategory?.ref || `existing-category-${existingCategory.id}`,
      name: existingCategory.name,
      color: existingCategory.colorCode,
      source: 'existing',
      existingCategory,
    };
  };

  const categoryOptions: CategoryOption[] = preview
    ? [
        ...preview.categories.map((category) => ({
          id: `suggestion-${category.ref}`,
          ref: category.ref,
          name: category.name,
          color:
            category.action === 'match'
              ? existingCategories.find(({ id }) => id === category.existingId)
                  ?.colorCode
              : undefined,
          source:
            category.action === 'match'
              ? ('existing' as const)
              : ('ai' as const),
          existingCategory:
            category.action === 'match'
              ? existingCategories.find(({ id }) => id === category.existingId)
              : undefined,
        })),
        ...existingCategories
          .filter(
            (existingCategory) =>
              !preview.categories.some(
                (category) => category.existingId === existingCategory.id
              )
          )
          .map(existingCategoryOption),
      ]
    : [];
  const aiCategoryOptions = categoryOptions
    .filter(({ source }) => source === 'ai')
    .map((category) => ({ ...category, label: category.name }));
  const categoryOptionFromSelection = (
    selection: ExistingCategory | CategoryOption
  ) =>
    'source' in selection
      ? selection
      : existingCategoryOption(selection as ExistingCategory);

  const hasSuggestions =
    !!preview &&
    !!(
      preview.categories.length ||
      preview.profiles.length ||
      preview.competences.length
    );
  const categoryDeleteItem =
    deleteItem && !isCompetence(deleteItem) && !isProfile(deleteItem)
      ? deleteItem
      : null;
  const categoryDeleteCompetences =
    categoryDeleteItem && preview
      ? preview.competences.filter(
          (competence) => competence.categoryRef === categoryDeleteItem.ref
        )
      : [];
  const replacementCategoryOptions = categoryDeleteItem
    ? categoryOptions.filter(({ ref }) => ref !== categoryDeleteItem.ref)
    : [];

  return (
    <div className="fixed inset-0 z-10 flex h-full min-h-[100vh] min-w-[100vw] flex-col bg-white">
      <div className="flex min-h-16 items-center justify-start">
        <IconButton
          className="my-auto mx-6"
          Icon={CrossFilled}
          variant="ghost"
          onClick={closePage}
          aria-label={t('pages.manage_competencies.import.close')}
        />
        <div>
          <h1 className="text-base font-semibold text-black">
            <T>{t('pages.manage_competencies.import.title')}</T>
          </h1>
          <p className="text-sm text-grey-06">
            {t('pages.manage_competencies.import.subtitle')}
          </p>
        </div>
      </div>

      <div className="flex h-full flex-col border-t border-grey-02">
        <Stepper>
          <Stepper.Item
            isActive={!preview}
            isDone={!!preview}
            onClick={() => {
              if (preview && !isImporting) reset();
            }}
            step="1"
          >
            {(!isMobile || !preview) && (
              <T>{t('pages.manage_competencies.import.step_upload')}</T>
            )}
          </Stepper.Item>
          <Stepper.Item
            isActive={!!preview && activeStep === 'review'}
            isDone={activeStep === 'teo-links'}
            onClick={() => {
              if (preview && !isImporting && !isLinkingTeoCompetences) {
                setActiveStep('review');
              }
            }}
            step="2"
          >
            {(!isMobile || !!preview) && (
              <T>{t('pages.manage_competencies.import.step_review')}</T>
            )}
          </Stepper.Item>
          <Stepper.Item
            isActive={!!preview && activeStep === 'teo-links'}
            onClick={() => undefined}
            step="3"
          >
            {(!isMobile || activeStep === 'teo-links') && (
              <T>{t('pages.manage_competencies.import.step_teo_links')}</T>
            )}
          </Stepper.Item>
        </Stepper>

        <div className="h-full max-h-[calc(100vh_-_225px)] overflow-y-auto py-8">
          <div className="mx-auto flex max-w-screen-md flex-col justify-center px-3">
            {!preview ? (
              <section className="rounded-2xl border border-grey-02 bg-white p-6 shadow-sm sm:p-8">
                <div className="max-w-2xl">
                  <h2 className="text-lg font-semibold text-black">
                    {t('pages.manage_competencies.import.upload_heading')}
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-grey-06">
                    {t('pages.manage_competencies.import.upload_description')}
                  </p>
                </div>

                <input
                  ref={inputRef}
                  className="hidden"
                  type="file"
                  disabled={isUploading}
                  onChange={(event) => selectFile(event.target.files?.[0])}
                />

                <div
                  className={`mt-6 flex min-h-[230px] flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 text-center transition-colors ${
                    isDragging
                      ? 'border-primary-05 bg-[#f3f7ff]'
                      : 'border-grey-03 bg-grey-01'
                  }`}
                  onDragEnter={(event) => {
                    event.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragOver={(event) => event.preventDefault()}
                  onDragLeave={(event) => {
                    event.preventDefault();
                    setIsDragging(false);
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    setIsDragging(false);
                    if (!isUploading) {
                      selectFile(event.dataTransfer.files?.[0]);
                    }
                  }}
                >
                  {isUploading ? (
                    <div
                      className="flex flex-col items-center"
                      role="status"
                      aria-live="polite"
                    >
                      <img
                        src="/images/icons/chatgpt.svg"
                        alt=""
                        className="h-8 w-8"
                      />
                      <div className="spinner-chat mt-4" aria-hidden="true">
                        <div className="bounce1" />
                        <div className="bounce2" />
                        <div className="bounce3" />
                      </div>
                      <p className="mt-3 text-sm font-semibold text-black">
                        {t('pages.manage_competencies.import.processing')}
                      </p>
                    </div>
                  ) : file ? (
                    <>
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white text-primary-05 shadow-sm">
                        <Document className="h-6 w-6" />
                      </div>
                      <p className="mt-4 max-w-full truncate text-sm font-semibold text-black">
                        {file.name}
                      </p>
                      <p className="mt-1 text-xs text-grey-06">
                        {getExtension(file.name)
                          ? `${getExtension(file.name).toUpperCase()} · `
                          : ''}
                        {formatFileSize(file.size)}
                      </p>
                      <Button
                        className="mt-4"
                        variant="ghost"
                        size="sm"
                        onClick={() => inputRef.current?.click()}
                      >
                        {t('pages.manage_competencies.import.replace_file')}
                      </Button>
                    </>
                  ) : (
                    <>
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white text-primary-05 shadow-sm">
                        <Upload className="h-6 w-6" />
                      </div>
                      <p className="mt-4 text-sm font-semibold text-black">
                        {t('pages.manage_competencies.import.drop_file')}
                      </p>
                      <p className="mt-1 text-sm text-grey-06">
                        {t('pages.manage_competencies.import.or_choose')}
                      </p>
                      <Button
                        className="mt-4"
                        variant="outline"
                        size="sm"
                        onClick={() => inputRef.current?.click()}
                      >
                        {t('pages.manage_competencies.import.choose_file')}
                      </Button>
                      <p className="mt-4 text-xs text-grey-05">
                        {t('pages.manage_competencies.import.formats')}
                      </p>
                    </>
                  )}
                </div>

                {error && (
                  <p className="mt-3 rounded-lg bg-[#fff2f1] px-4 py-3 text-sm text-error-04">
                    {error}
                  </p>
                )}
              </section>
            ) : activeStep === 'review' ? (
              isLinkingTeoCompetences ? (
                <section className="rounded-2xl border border-grey-02 bg-white p-6 shadow-sm sm:p-8">
                  <div
                    className="flex min-h-[230px] flex-col items-center justify-center rounded-xl border-2 border-dashed border-grey-03 bg-grey-01 px-6 text-center"
                    role="status"
                    aria-live="polite"
                    aria-busy="true"
                  >
                    <img
                      src="/images/icons/chatgpt.svg"
                      alt=""
                      className="h-8 w-8"
                    />
                    <div className="spinner-chat mt-4" aria-hidden="true">
                      <div className="bounce1" />
                      <div className="bounce2" />
                      <div className="bounce3" />
                    </div>
                    <p className="mt-3 text-sm font-semibold text-black">
                      {t('pages.manage_competencies.import.teo_links_loading')}
                    </p>
                  </div>
                </section>
              ) : (
                <div>
                  <div className="mb-6 rounded-xl border border-[#cbdaf7] bg-[#f3f7ff] p-4">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-[#dfeaff] px-2.5 py-1 text-xs font-semibold text-[#315da8]">
                          {t('pages.manage_competencies.import.ai_badge')}
                        </span>
                        <span className="text-sm font-semibold text-black">
                          {preview.source.fileName}
                        </span>
                        <span className="text-xs text-grey-06">
                          {formatFileSize(preview.source.fileSize)}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-grey-06">
                        {t('pages.manage_competencies.import.ai_notice')}
                      </p>
                    </div>
                  </div>

                  <div className="mb-8 grid gap-3 sm:grid-cols-3">
                    {(
                      [
                        ['categories', preview.categories.length],
                        ['profiles', preview.profiles.length],
                        ['competences', preview.competences.length],
                      ] as const
                    ).map(([label, count]) => (
                      <div
                        key={label}
                        className="rounded-xl border border-grey-02 bg-white px-5 py-4"
                      >
                        <p className="text-2xl font-semibold text-black">
                          {count}
                        </p>
                        <p className="mt-1 text-sm text-grey-06">
                          {t(`pages.manage_competencies.import.${label}`)}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-8">
                    <section>
                      <div className="mb-3 flex items-baseline gap-3">
                        <span className="text-sm font-semibold text-primary-05">
                          01
                        </span>
                        <h2 className="text-base font-semibold text-black">
                          {t('pages.manage_competencies.import.categories')}
                        </h2>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {preview.categories.map((category) => (
                          <SuggestionCard
                            key={category.ref}
                            item={category}
                            onEdit={
                              !importResult && category.action === 'create'
                                ? () => openEditor(category)
                                : undefined
                            }
                            onDelete={
                              !importResult
                                ? () => openDelete(category)
                                : undefined
                            }
                          />
                        ))}
                      </div>
                    </section>

                    <section>
                      <div className="mb-3 flex items-baseline gap-3">
                        <span className="text-sm font-semibold text-primary-05">
                          02
                        </span>
                        <h2 className="text-base font-semibold text-black">
                          {t('pages.manage_competencies.import.profiles')}
                        </h2>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {preview.profiles.map((profile) => (
                          <SuggestionCard
                            key={profile.ref}
                            item={profile}
                            onEdit={
                              !importResult
                                ? () => openEditor(profile)
                                : undefined
                            }
                            onDelete={
                              !importResult
                                ? () => openDelete(profile)
                                : undefined
                            }
                            competenceNames={competenceNames(profile.ref)}
                          />
                        ))}
                      </div>
                    </section>

                    <section>
                      <div className="mb-3 flex items-baseline gap-3">
                        <span className="text-sm font-semibold text-primary-05">
                          03
                        </span>
                        <h2 className="text-base font-semibold text-black">
                          {t('pages.manage_competencies.import.competences')}
                        </h2>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {preview.competences.map((competence) => (
                          <SuggestionCard
                            key={competence.ref}
                            item={competence}
                            categoryName={categoryName(competence.categoryRef)}
                            profileNames={profileNames(competence.profileRefs)}
                            onEdit={
                              !importResult
                                ? () => openEditor(competence)
                                : undefined
                            }
                            onDelete={
                              !importResult
                                ? () => openDelete(competence)
                                : undefined
                            }
                          />
                        ))}
                      </div>
                    </section>
                  </div>

                  <div className="mt-8 border-t border-grey-02 pt-6">
                    {importResult && (
                      <div className="mb-4 rounded-xl border border-[#b9dfc9] bg-[#eef9f2] p-4">
                        <p className="text-sm font-semibold text-[#237a4b]">
                          {t('pages.manage_competencies.import.import_success')}
                        </p>
                        <p className="mt-1 text-sm text-grey-07">
                          {t(
                            'pages.manage_competencies.import.import_success_summary',
                            {
                              categories:
                                importResult.summary.categoriesCreated,
                              profiles: importResult.summary.profilesCreated,
                              competences:
                                importResult.summary.competencesCreated,
                            }
                          )}
                        </p>
                      </div>
                    )}
                    {importError && (
                      <p className="mb-4 rounded-lg bg-[#fff2f1] px-4 py-3 text-sm text-error-04">
                        {importError}
                      </p>
                    )}
                    {teoLinkError && (
                      <p className="mb-4 rounded-lg bg-[#fff2f1] px-4 py-3 text-sm text-error-04">
                        {teoLinkError}
                      </p>
                    )}
                  </div>
                </div>
              )
            ) : (
              <div>
                <div className="mb-6 rounded-xl border border-[#cbdaf7] bg-[#f3f7ff] p-4">
                  <span className="rounded-full bg-[#dfeaff] px-2.5 py-1 text-xs font-semibold text-[#315da8]">
                    {t('pages.manage_competencies.import.ai_badge')}
                  </span>
                  <h2 className="mt-3 text-lg font-semibold text-black">
                    {t('pages.manage_competencies.import.teo_links_heading')}
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-grey-06">
                    {t(
                      'pages.manage_competencies.import.teo_links_description'
                    )}
                  </p>
                </div>

                <div className="space-y-4">
                  {preview.competences.map((competence) => {
                    const linkedIds = teoLinks[competence.ref] || [];
                    const linkedCompetences = teoCompetences
                      .filter(({ id }) => linkedIds.includes(id))
                      .map((teoCompetence) => ({
                        ...teoCompetence,
                        key: teoCompetence.id,
                      }));

                    return (
                      <section
                        key={competence.ref}
                        className="rounded-xl border border-grey-02 bg-white p-5"
                      >
                        <p className="text-base font-semibold text-black">
                          {competence.name}
                        </p>
                        <p className="mt-1 text-sm text-grey-06">
                          {competence.code}
                        </p>

                        <div className="relative z-[10] mt-4 max-w-[620px]">
                          <PaginationMultiSelect
                            value={linkedCompetences}
                            onChange={(
                              selection:
                                | TeoCompetence[]
                                | ((
                                    current: TeoCompetence[]
                                  ) => TeoCompetence[])
                            ) => {
                              const selected =
                                typeof selection === 'function'
                                  ? selection(linkedCompetences)
                                  : selection;
                              setTeoLinks((links) => ({
                                ...links,
                                [competence.ref]: selected.map(({ id }) => id),
                              }));
                            }}
                            keyFn={(teoCompetence: TeoCompetence) =>
                              teoCompetence.id
                            }
                            fetch={useGet.bind(null, '/competences', {
                              organisationId: 'null',
                              lang: i18n.language,
                              disabled: false,
                            })}
                            defaultSort={[{ id: 'name', desc: false }]}
                            positionDropdown="top"
                            render={(teoCompetence: TeoCompetence) =>
                              teoCompetence.name
                            }
                          />
                        </div>
                      </section>
                    );
                  })}
                </div>

                {teoLinkError && (
                  <p className="mt-4 rounded-lg bg-[#fff2f1] px-4 py-3 text-sm text-error-04">
                    {teoLinkError}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mt-auto flex border-t border-grey-02 py-5">
          <div className="mx-auto flex w-full max-w-screen-md justify-end px-3">
            {!preview ? (
              <Button disabled={!file || isUploading} onClick={createPreview}>
                {isUploading
                  ? t('pages.manage_competencies.import.processing')
                  : t('pages.manage_competencies.import.process_file')}
              </Button>
            ) : activeStep === 'review' ? (
              <>
                <Button
                  variant="secondary"
                  disabled={isImporting || isLinkingTeoCompetences}
                  onClick={closePage}
                  className="mr-auto"
                >
                  {t('pages.manage_competencies.import.cancel')}
                </Button>
                <Button
                  variant="ghost"
                  disabled={isImporting || !!importResult || !hasSuggestions}
                  onClick={() => {
                    setTeoLinks({});
                    setIsConfirmOpen(true);
                  }}
                >
                  {t('pages.manage_competencies.import.skip_teo_links')}
                </Button>
                <Button
                  disabled={
                    isImporting ||
                    isLinkingTeoCompetences ||
                    !!importResult ||
                    !preview.competences.length
                  }
                  onClick={startTeoLinking}
                >
                  {isLinkingTeoCompetences
                    ? t('pages.manage_competencies.import.teo_links_loading')
                    : t('pages.manage_competencies.import.continue_teo_links')}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="secondary"
                  disabled={isImporting}
                  onClick={() => setActiveStep('review')}
                  className="mr-auto"
                >
                  {t('pages.manage_competencies.import.back_to_review')}
                </Button>
                <Button
                  variant="ghost"
                  disabled={isImporting}
                  onClick={() => {
                    setTeoLinks({});
                    setIsConfirmOpen(true);
                  }}
                >
                  {t('pages.manage_competencies.import.skip_teo_links')}
                </Button>
                <Button
                  disabled={isImporting || !!importResult}
                  onClick={() => setIsConfirmOpen(true)}
                  className="ml-3"
                >
                  {t('pages.manage_competencies.import.confirm_import')}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {editItem && (
        <Modal isOpen>
          <Modal.Header onClose={closeEditor}>
            {isCompetence(editItem)
              ? t('pages.manage_competencies.import.edit_competence_title')
              : isProfile(editItem)
              ? t('pages.manage_competencies.import.edit_profile_title')
              : t('pages.manage_competencies.import.edit_category_title')}
          </Modal.Header>
          <Modal.Body>
            <form
              className="w-full max-w-[520px]"
              onSubmit={(event) => {
                event.preventDefault();
                saveEdit();
              }}
            >
              <div>
                <Label htmlFor="import-edit-name">
                  <span className="text-sm font-semibold">
                    {t('pages.manage_competencies.import.name_label')}
                  </span>
                </Label>
                <InputText
                  id="import-edit-name"
                  value={editItem.name}
                  maxLength={
                    isCompetence(editItem)
                      ? 1000
                      : isProfile(editItem)
                      ? 511
                      : 255
                  }
                  onChange={(event) => {
                    setEditError('');
                    setEditItem({ ...editItem, name: event.target.value });
                  }}
                />
              </div>

              <div className="mt-5">
                <Label htmlFor="import-edit-description">
                  <span className="text-sm font-semibold">
                    {t('pages.manage_competencies.import.description_label')}
                  </span>
                </Label>
                <InputTextarea
                  id="import-edit-description"
                  value={editItem.description}
                  onChange={(event) =>
                    setEditItem({
                      ...editItem,
                      description: event.target.value,
                    })
                  }
                />
              </div>

              {isCompetence(editItem) && (
                <div className="relative z-[10] mt-5">
                  <Label htmlFor="import-edit-category">
                    <span className="text-sm font-semibold">
                      {t('pages.manage_competencies.import.category_label')}
                    </span>
                  </Label>
                  <div id="import-edit-category">
                    <PaginationSingleSelect
                      onChange={(
                        selection: ExistingCategory | CategoryOption
                      ) => {
                        const option = categoryOptionFromSelection(selection);
                        setEditError('');
                        setSelectedEditCategory(option);
                        setEditItem({
                          ...editItem,
                          categoryRef: option.ref,
                        });
                      }}
                      fetch={useGet.bind(null, '/competencecategories', {
                        organisationId: `${organisationId},OR,organisationId=null`,
                        lang: i18n.language,
                      })}
                      defaultSort={[{ id: 'name', desc: false }]}
                      render={(category: ExistingCategory | CategoryOption) =>
                        category.name
                      }
                      supplementalOptions={aiCategoryOptions}
                      placeholder={t(
                        'pages.manage_competencies.import.select_category'
                      )}
                      value={
                        selectedEditCategory ||
                        categoryOptions.find(
                          ({ ref }) => ref === editItem.categoryRef
                        )
                      }
                      displayValue={(category: CategoryOption) => category.name}
                    >
                      {(option: CategoryOption) => (
                        <div className="flex w-full min-w-0 items-center">
                          <span
                            style={{
                              backgroundColor: option.color || undefined,
                            }}
                            className="mr-2 h-2 w-2 shrink-0 rounded-full bg-grey-03"
                          />
                          <span className="truncate">{option.name}</span>
                          <span className="ml-auto pl-3 text-xs text-grey-05">
                            {option.source === 'ai'
                              ? t(
                                  'pages.manage_competencies.import.ai_generated'
                                )
                              : t(
                                  'pages.manage_competencies.import.existing_category'
                                )}
                          </span>
                        </div>
                      )}
                    </PaginationSingleSelect>
                  </div>
                </div>
              )}

              {'profileRefs' in editItem && (
                <div className="mt-5">
                  <CompetenceValidityFields
                    duration={editValidityDuration}
                    expiryDate={editExpiryDate}
                    onDurationChange={setEditValidityDuration}
                    onExpiryDateChange={setEditExpiryDate}
                    onValidForDaysChange={setEditValidForDays}
                    validForDays={editValidForDays}
                  />
                </div>
              )}

              <div className="mt-5">
                <Label htmlFor="import-edit-code">
                  <span className="text-sm font-semibold">
                    {t('pages.manage_competencies.import.code_label')}
                  </span>
                </Label>
                <InputText
                  id="import-edit-code"
                  value={editItem.code}
                  maxLength={
                    isCompetence(editItem) || isProfile(editItem) ? 64 : 32
                  }
                  onChange={(event) => {
                    setEditError('');
                    setEditItem({ ...editItem, code: event.target.value });
                  }}
                />
                <p className="mt-1 text-xs text-grey-05">
                  {t('pages.manage_competencies.import.code_help')}
                </p>
              </div>

              {editError && (
                <p className="mt-4 rounded-lg bg-[#fff2f1] px-4 py-3 text-sm text-error-04">
                  {editError}
                </p>
              )}
            </form>
          </Modal.Body>
          <Modal.Footer>
            <div className="flex w-full justify-end gap-3">
              <Button variant="ghost" onClick={closeEditor}>
                {t('pages.manage_competencies.import.cancel')}
              </Button>
              <Button onClick={saveEdit}>
                {t('pages.manage_competencies.import.save_changes')}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      )}

      {deleteItem && preview && (
        <ComponentDeleteModal
          closeModal={() => {
            setDeleteItem(null);
            setCategoryReassignments({});
          }}
          setIsOpenDeleteModal={(open: boolean) => {
            if (!open) {
              setDeleteItem(null);
              setCategoryReassignments({});
            }
          }}
          title={t('pages.manage_competencies.import.delete_title')}
          heading={t('pages.manage_competencies.import.delete_heading', {
            name: deleteItem.name,
          })}
          text={
            categoryDeleteItem
              ? undefined
              : t('pages.manage_competencies.import.delete_description')
          }
          modalClassName={categoryDeleteItem ? 'max-w-3xl' : undefined}
          deleteDisabled={categoryDeleteCompetences.some(
            (competence) => !categoryReassignments[competence.ref]
          )}
          handleDelete={() => {
            setPreview((currentPreview) => {
              if (!currentPreview) return currentPreview;

              const previewWithReplacementCategories = Object.entries(
                categoryReassignments
              ).reduce(
                (updatedPreview, [competenceRef, categoryRef]) =>
                  addMatchedCategory(
                    updatedPreview,
                    categoryReassignmentOptions[competenceRef] ||
                      categoryOptions.find(({ ref }) => ref === categoryRef)
                  ),
                currentPreview
              );

              return removePreviewItem(
                previewWithReplacementCategories,
                deleteItem,
                categoryReassignments
              );
            });
            setDeleteItem(null);
            setCategoryReassignments({});
            setCategoryReassignmentOptions({});
            setTeoLinks({});
          }}
        >
          {categoryDeleteCompetences.length > 0 && (
            <div className="border-t border-grey-02 pt-5">
              <p className="mb-4 text-sm font-semibold text-black">
                {t('pages.manage_competencies.import.delete_category_reassign')}
              </p>
              <div className="space-y-3 pr-1">
                {categoryDeleteCompetences.map((competence) => (
                  <div
                    key={competence.ref}
                    className="rounded-lg border border-grey-02 p-3"
                  >
                    <p className="mb-2 text-sm font-semibold text-black">
                      {competence.name}
                    </p>
                    <PaginationSingleSelect
                      portal
                      onChange={(
                        selection: ExistingCategory | CategoryOption
                      ) => {
                        const option = categoryOptionFromSelection(selection);
                        setCategoryReassignments((reassignments) => ({
                          ...reassignments,
                          [competence.ref]: option.ref,
                        }));
                        setCategoryReassignmentOptions((options) => ({
                          ...options,
                          [competence.ref]: option,
                        }));
                      }}
                      fetch={useGet.bind(null, '/competencecategories', {
                        organisationId: `${organisationId},OR,organisationId=null`,
                        lang: i18n.language,
                      })}
                      defaultSort={[{ id: 'name', desc: false }]}
                      render={(category: ExistingCategory | CategoryOption) =>
                        category.name
                      }
                      supplementalOptions={replacementCategoryOptions
                        .filter(({ source }) => source === 'ai')
                        .map((category) => ({
                          ...category,
                          label: category.name,
                        }))}
                      filterOption={(
                        category: ExistingCategory | CategoryOption
                      ) =>
                        !(
                          categoryDeleteItem?.action === 'match' &&
                          category.id === categoryDeleteItem.existingId
                        )
                      }
                      placeholder={t(
                        'pages.manage_competencies.import.select_category'
                      )}
                      value={
                        categoryReassignmentOptions[competence.ref] ||
                        replacementCategoryOptions.find(
                          ({ ref }) =>
                            ref === categoryReassignments[competence.ref]
                        )
                      }
                      displayValue={(category: CategoryOption) => category.name}
                    >
                      {(option: CategoryOption) => (
                        <div className="flex w-full min-w-0 items-center">
                          <span
                            style={{
                              backgroundColor: option.color || undefined,
                            }}
                            className="mr-2 h-2 w-2 shrink-0 rounded-full bg-grey-03"
                          />
                          <span className="truncate">{option.name}</span>
                          <span className="ml-auto pl-3 text-xs text-grey-05">
                            {option.source === 'ai'
                              ? t(
                                  'pages.manage_competencies.import.ai_generated'
                                )
                              : t(
                                  'pages.manage_competencies.import.existing_category'
                                )}
                          </span>
                        </div>
                      )}
                    </PaginationSingleSelect>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ComponentDeleteModal>
      )}

      {isConfirmOpen && preview && (
        <Modal isOpen>
          <Modal.Header
            onClose={() => {
              if (!isImporting) setIsConfirmOpen(false);
            }}
          >
            {t('pages.manage_competencies.import.confirm_title')}
          </Modal.Header>
          <Modal.Body>
            <p className="text-sm leading-6 text-grey-07">
              {t('pages.manage_competencies.import.confirm_description', {
                categories: preview.categories.filter(
                  ({ action }) => action === 'create'
                ).length,
                profiles: preview.profiles.length,
                competences: preview.competences.length,
              })}
            </p>
            <p className="mt-3 text-sm font-semibold text-black">
              {t('pages.manage_competencies.import.confirm_warning')}
            </p>
          </Modal.Body>
          <Modal.Footer>
            <div className="flex w-full justify-end gap-3">
              <Button
                variant="ghost"
                disabled={isImporting}
                onClick={() => setIsConfirmOpen(false)}
              >
                {t('pages.manage_competencies.import.cancel')}
              </Button>
              <Button disabled={isImporting} onClick={confirmImport}>
                {isImporting
                  ? t('pages.manage_competencies.import.importing')
                  : t('pages.manage_competencies.import.confirm_and_import')}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}

export default CompetencyImportPage;
